import type { AccountState, AnalyticsProperties } from "@-/dev.improve.haniverse.lib.analytics";

export type LocalAccountStatus = "sessionExpired" | "signedIn" | "unverified";

/** The learner settings worth breaking retention down by (a subset of the actor `user` row). */
export type AnalyticsUserSettings = {
  hanziScript: string;
  hskVersion: string;
  isMicDrillsEnabled: boolean;
  isPinyinFlashcardsEnabled: boolean;
  isSentenceCharacterDrillEnabled: boolean;
  isSentenceSpokenDrillEnabled: boolean;
  isToneColorsEnabled: boolean;
  isWordSentenceCardsEnabled: boolean;
  reviewMode: string;
};

export function accountStateFromStatus(status: LocalAccountStatus | null): AccountState {
  switch (status) {
    case null: {
      return "guest";
    }
    case "signedIn": {
      return "signed_in";
    }
    case "sessionExpired": {
      return "session_expired";
    }
    case "unverified": {
      return "unverified";
    }
  }
}

/** Ride on every event (including `$screen` and lifecycle), so any chart can split by them. */
export function buildAnalyticsSuperProperties({
  accountStatus,
  hanziScript,
  isPremium,
}: {
  accountStatus: LocalAccountStatus | null;
  hanziScript: string;
  isPremium: boolean;
}): AnalyticsProperties {
  return {
    account_state: accountStateFromStatus(accountStatus),
    hanzi_script: hanziScript,
    is_premium: isPremium,
  };
}

/** Describe the learner now. `null` progress means it has not loaded yet: those keys are omitted. */
export function buildAnalyticsPersonProperties({
  charactersLearned,
  currentLevelIndex,
  isPremium,
  slideTextSize,
  user,
}: {
  charactersLearned: number | null;
  currentLevelIndex: number | null | undefined;
  isPremium: boolean;
  slideTextSize: string;
  user: AnalyticsUserSettings;
}): AnalyticsProperties {
  return {
    hanzi_script: user.hanziScript,
    hsk_version: user.hskVersion,
    is_mic_drills_enabled: user.isMicDrillsEnabled,
    is_pinyin_flashcards_enabled: user.isPinyinFlashcardsEnabled,
    is_premium: isPremium,
    is_sentence_character_drill_enabled: user.isSentenceCharacterDrillEnabled,
    is_sentence_spoken_drill_enabled: user.isSentenceSpokenDrillEnabled,
    is_tone_colors_enabled: user.isToneColorsEnabled,
    is_word_sentence_cards_enabled: user.isWordSentenceCardsEnabled,
    review_mode: user.reviewMode,
    slide_text_size: slideTextSize,
    ...(charactersLearned === null ? {} : { characters_learned: charactersLearned }),
    // `null` once every level is done; `undefined` while progress is still loading.
    ...(currentLevelIndex === undefined ? {} : { current_level: currentLevelIndex }),
  };
}
